import { Button } from "../components/ui/button";
import { IMAGES, RESTAURANT } from "../lib/restaurant";
import { Pizza, Utensils, Wine, Coffee, MapPin, Clock, Heart, Leaf } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { Link } from "react-router-dom";

const HL_ICONS = [Pizza, Utensils, Wine, Coffee];

export default function About() {
  const { t } = useLang();
  return (
    <div data-testid="about-page" className="pt-20">
      <section className="bg-charcoal text-cream py-24 sm:py-32 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <p className="uppercase-track text-xs text-gold mb-6">{t.about.eyebrow}</p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05]">
              {t.about.title1}<br /><span className="italic text-gold">{t.about.title2}</span>
            </h1>
          </div>
          <p className="lg:col-span-5 text-cream/80 leading-relaxed text-lg">{t.about.lead}</p>
        </div>
      </section>

      <section className="bg-cream py-24">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="uppercase-track text-xs text-wine mb-5">{t.about.storyEyebrow}</p>
            <h2 className="font-serif text-4xl text-charcoal leading-tight">{t.about.storyTitle1}<br />{t.about.storyTitle2}</h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-charcoal/80 text-lg leading-relaxed">
            <p>{t.about.storyP1}</p>
            <p>{t.about.storyP2}</p>
            <p>{t.about.storyP3}</p>
          </div>
        </div>
      </section>

      <section className="bg-charcoal text-cream py-14 border-y border-cream/10">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-12 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { n: "28+", l: t.about.stat1 },
            { n: "8",   l: t.about.stat2 },
            { n: "16",  l: t.about.stat3 },
            { n: "★",   l: t.about.stat4 },
          ].map((s, i) => (
            <div key={i}>
              <div className="font-serif text-4xl sm:text-5xl text-gold mb-2">{s.n}</div>
              <p className="text-xs uppercase-track text-cream/85">{s.l}</p>
            </div>
          ))}
        </div>
      </section>

      <section>
        <img src={IMAGES.muralDay} alt="Segreto interior" className="w-full max-h-[80vh] object-cover" data-testid="about-interior-image" />
      </section>

      <section className="bg-cream py-24">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="max-w-2xl mb-14">
            <p className="uppercase-track text-xs text-wine mb-5">{t.about.highlightsEyebrow}</p>
            <h2 className="font-serif text-4xl sm:text-5xl text-charcoal leading-tight">{t.about.highlightsTitle}</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.about.highlights.map((h, i) => {
              const Ic = HL_ICONS[i] || Utensils;
              return (
                <div key={i} className="border-t-2 border-wine pt-6">
                  <Ic className="h-8 w-8 text-wine mb-4" />
                  <h3 className="font-serif text-2xl text-charcoal mb-3">{h.title}</h3>
                  <p className="text-charcoal/70 text-sm leading-relaxed">{h.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-charcoal text-cream py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="max-w-2xl mb-14">
            <p className="uppercase-track text-xs text-gold mb-5">{t.about.valuesEyebrow}</p>
            <h2 className="font-serif text-4xl sm:text-5xl leading-tight">{t.about.valuesTitle}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-px bg-cream/10">
            {t.about.values.map((v, i) => (
              <div key={i} className="bg-charcoal p-8 sm:p-10">
                <div className="font-serif text-5xl text-gold mb-4">0{i+1}</div>
                <h3 className="font-serif text-2xl mb-3">{v.title}</h3>
                <p className="text-cream/85 leading-relaxed text-sm">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-cream py-20">
        <div className="max-w-[1000px] mx-auto px-5 sm:px-8 lg:px-12 text-center">
          <Heart className="h-7 w-7 text-wine mx-auto mb-6" />
          <p className="font-serif italic text-3xl sm:text-4xl text-charcoal leading-snug">{t.about.quote}</p>
        </div>
      </section>

      <section className="bg-cream pb-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-12 grid sm:grid-cols-3 gap-8 border-t border-charcoal/15 pt-12">
          <div>
            <MapPin className="h-6 w-6 text-wine mb-3" />
            <h4 className="font-serif text-xl text-charcoal mb-2">{t.about.info1Title}</h4>
            <p className="text-charcoal/70 text-sm leading-relaxed">{RESTAURANT.address.street}<br />{RESTAURANT.address.postal}<br />{RESTAURANT.address.district}</p>
          </div>
          <div>
            <Clock className="h-6 w-6 text-wine mb-3" />
            <h4 className="font-serif text-xl text-charcoal mb-2">{t.about.info2Title}</h4>
            <ul className="text-charcoal/70 text-sm space-y-1">
              <li className="flex justify-between"><span>{t.about.hoursShort.mon}</span><span>{t.about.hoursShort.monVal}</span></li>
              <li className="flex justify-between"><span>{t.about.hoursShort.weekday}</span><span>{t.about.hoursShort.weekdayVal}</span></li>
              <li className="flex justify-between"><span>{t.about.hoursShort.weekend}</span><span>{t.about.hoursShort.weekendVal}</span></li>
            </ul>
          </div>
          <div>
            <Leaf className="h-6 w-6 text-wine mb-3" />
            <h4 className="font-serif text-xl text-charcoal mb-2">{t.about.info3Title}</h4>
            <p className="text-charcoal/70 text-sm leading-relaxed">{t.about.info3Text}</p>
          </div>
        </div>
      </section>

      <section className="bg-charcoal text-cream py-24 text-center">
        <div className="max-w-[900px] mx-auto px-5 sm:px-8 lg:px-12">
          <h2 className="font-serif text-4xl sm:text-5xl mb-6">{t.about.ctaTitle}</h2>
          <p className="text-cream/85 mb-10 max-w-xl mx-auto leading-relaxed">{t.about.ctaText}</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="tel:+48518805767">
              <Button data-testid="about-reserve" className="rounded-none bg-wine hover:bg-wine/85 text-cream uppercase-track text-xs h-12 px-8">{t.common.callToReserve}</Button>
            </a>
            <Link to="/menu">
              <Button data-testid="about-menu" variant="outline" className="rounded-none bg-transparent border-cream text-cream hover:bg-cream hover:text-charcoal uppercase-track text-xs h-12 px-8">{t.common.viewMenu}</Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
